import { 
    Box,
    IconButton,
    Tooltip
} from "@mui/material"

import {
    ContentCopy as ContentCopyIcon,
    DriveFileMove as DriveFileMoveIcon
} from '@mui/icons-material';

import dayjs from 'dayjs'

import AvatarName from "../AvatarName"

import { formatCurrency } from "./functions";
import KwikMarkdown from "../../utils/kwikmarkdown";
import { getStandardFieldLabel, isStandardFieldSummaryVisible } from "./standard-fields";

const extractParentId = (target) => {
    return Number(target?.parentElement?.parentElement?.parentElement?.attributes['data-rbd-droppable-id']?.value || 0)
}

export const currencyPrefix = {
    "BRL": "R$ "
}

export const getCustomFieldVisibilityKey = (fieldId) => `custom_field_${fieldId}`

const hasSummaryValue = (value) => {
    if (value === null || value === undefined) return false
    if (typeof value === "string") return value.trim() !== ""
    return true
}

const renderSummaryValue = (field) => {
    const value = field.display_value ?? field.value
    if (field.type === "checkbox" || field.field_type === "checkbox") {
        return [true, "true", "1", 1, "Sim"].includes(value) ? "Sim" : "Não"
    }
    return value
}

const getSummaryCustomFields = (card) => {
    const customFieldsSnapshot = (
        card?.custom_fields_snapshot &&
        typeof card.custom_fields_snapshot === "object" &&
        !Array.isArray(card.custom_fields_snapshot)
    ) ? card.custom_fields_snapshot : {}

    const snapshotFields = Object.entries(customFieldsSnapshot)
        .filter(([, field]) => field?.show_in_summary && hasSummaryValue(field?.value))
        .map(([id, field]) => ({ ...field, id }))

    const customFields = Array.isArray(card?.custom_fields) ? card.custom_fields : []
    const snapshotFieldIds = new Set(snapshotFields.map(field => String(field.id)))
    const liveFields = customFields
        .filter(field => (
            field?.field_show_in_summary &&
            !snapshotFieldIds.has(String(field.field)) &&
            hasSummaryValue(field?.display_value ?? field?.value)
        ))
        .map(field => ({
            id: field.field,
            name: field.field_name,
            type: field.field_type,
            value: field.display_value ?? field.value,
        }))

    return [...snapshotFields, ...liveFields]
}

const SingleCard = ({ card, onClick, onCopy, onMove, journey, visibleFields = {}, latestChanged = null }) => {

    const summaryCustomFields = getSummaryCustomFields(card)
        .filter(field => visibleFields[getCustomFieldVisibilityKey(field.id)] !== false)
    const summaryVisible = (key) => isStandardFieldSummaryVisible(journey, key) && visibleFields[key] !== false
    const fieldLabel = (key) => getStandardFieldLabel(journey, key)

    return (
        <Box onClick={
            ({currentTarget}) => onClick(extractParentId(currentTarget))}
            className={
                (latestChanged == card.id)
                    ? "journey-card updated"
                    : (card.status === "CONQUERED")
                        ? "journey-card green"
                        : (card.status === "LOST")
                            ? "journey-card red"
                            : "journey-card"
            }>
            <Box className='journey-card-header'>
                <Box className="journey-card-title">{card.title}</Box>
                <Box className="journey-card-actions">
                    <Tooltip title="Copiar para outra jornada">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy?.(card); }}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Mover para outra jornada">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMove?.(card); }}>
                            <DriveFileMoveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            
            {card?.archived && (
                <p style={{
                    backgroundColor: "#f0f0f0",
                    padding: ".25rem .5rem",
                    borderRadius: ".25rem",
                    fontSize: ".8em",
                    color: "#888",
                    display: "inline-block",
                    width: "fit-content"
                }}>
                    Arquivado
                </p>
            )}

            {card?.tags?.length > 0 && (
                <Box className='journey-card-labellist'>
                    {card.tags.map((tag) => (
                        <Box
                            key={tag.id || tag.name}
                            sx={{
                                padding: ".15rem .25rem",
                                borderRadius: ".25rem",
                                fontSize: ".8em",
                                backgroundColor: tag.color,
                            }}
                        >
                            {tag.name}
                        </Box>
                    ))}
                </Box>
            )}

            {summaryVisible("description") && card.description && (
                <Box className='journey-card-description'>
                    <KwikMarkdown>{card.description}</KwikMarkdown>
                </Box>
            )}
            
            {summaryVisible("value") && (
                <Box className='journey-card-value'>
                    <span>{fieldLabel("value")}:</span>
                    <span>{(card.value) ? `${currencyPrefix[card.currency]}${formatCurrency(parseFloat(card.value * 100), card.currency)}` : ""}</span>
                </Box>
            )}

            {summaryVisible("owner") && (
                <Box className='journey-card-seller'>
                    <span>{fieldLabel("owner")}:</span>
                    <span className='avatarflex'>{card.owner_name}</span>
                </Box>
            )}

            {summaryVisible("contact") && (
                <Box className='journey-card-contact'>
                    <span>{fieldLabel("contact")}:</span>
                    <span className='avatarflex'>{card.contact}</span>
                </Box>
            )}
            
            {summaryVisible("contact_enterprise") && (card.contact_enterprise_name || card.contact_enterprise?.name) && (
                <Box className='journey-card-enterprise'>
                    <span>{fieldLabel("contact_enterprise")}:</span>
                    <span className='avatarflex'>
                        {card.contact_enterprise_name || card.contact_enterprise?.name}
                    </span>
                </Box>
            )}
            
            {summaryVisible("created_at") && (
                <Box className='journey-card-date'>
                    <span>{fieldLabel("created_at")}:</span>
                    <span>{(card.created_at) ? dayjs.utc(card.created_at).local().format('DD/MM/YYYY HH:mm') : ""}</span>
                </Box>
            )}
        
            {summaryVisible("created_by") && card.created_by_name && (
                <Box className='journey-card-seller'>
                    <span>{fieldLabel("created_by")}:</span>
                    <span className='avatarflex'>{card.created_by_name}</span>
                </Box>
            )}
            
            {summaryVisible("estimated_at") && (
                <Box className='journey-card-date'>
                    <span>{fieldLabel("estimated_at")}:</span>
                    <span>{(card.estimated_at) ? dayjs.utc(card.estimated_at).local().format('DD/MM/YYYY HH:mm') : ""}</span>
                </Box>
            )}

            {summaryVisible("closed_at") && (
                <Box className='journey-card-date'>
                    <span>{fieldLabel("closed_at")}:</span>
                    <span>{(card.closed_at) ? dayjs.utc(card.closed_at).local().format('DD/MM/YYYY HH:mm') : ""}</span>
                </Box>
            )}
            
            {summaryCustomFields.map((field) => (
                <Box key={getCustomFieldVisibilityKey(field.id)} className='journey-card-custom-field'>
                    <span>{field.name}:</span><span>{renderSummaryValue(field)}</span>
                </Box>
            ))}
        </Box>
    )
}

export default SingleCard
